import React, { useId } from 'react';
import { dashArray, type Symbol } from '@/utils/layerStyle';

/**
 * A small picture of one symbol of a layer's style - a marker at its size and turn, a line with its dash, casing and
 * arrow, an area with its fill pattern and outline - for the legend and the style editor. Drawn as SVG from the same
 * values the map draws with (utils/layerStyle.ts), so that the two never disagree.
 */

function markerPath(marker: string | null | undefined, r: number): React.ReactNode | null {
  const pts = (n: number, outer: number, inner: number | null, start: number) => {
    const out: string[] = [];
    const count = inner == null ? n : n * 2;
    for (let i = 0; i < count; i++) {
      const rad = inner == null || i % 2 === 0 ? outer : inner;
      const a = start + (i * Math.PI * 2) / count;
      out.push(`${(Math.cos(a) * rad).toFixed(2)},${(Math.sin(a) * rad).toFixed(2)}`);
    }
    return out.join(' ');
  };
  switch (marker) {
    case 'square': return <rect x={-r * 0.85} y={-r * 0.85} width={r * 1.7} height={r * 1.7} />;
    case 'diamond': return <polygon points={pts(4, r * 1.3, null, -Math.PI / 2)} />;
    case 'triangle': return <polygon points={pts(3, r * 1.3, null, -Math.PI / 2)} />;
    case 'star': return <polygon points={pts(5, r * 1.4, r * 0.6, -Math.PI / 2)} />;
    default: return null;
  }
}

export const StyleSwatch: React.FC<{ symbol: Symbol; width?: number; height?: number }> = ({ symbol: s, width = 28, height = 18 }) => {
  const id = useId().replace(/:/g, '');
  const opacity = s.opacity ?? 1;
  if (s.geometry === 'point') {
    const d = Math.max(5, Math.min(height + 8, s.size ?? 10));
    const r = d / 2;
    const turn = s.rotation ?? 0;
    const sw = s.strokeWidth ?? 1;
    if (s.marker === 'pin') {
      const h = Math.min(height, d * 1.6);
      return (
        <svg width={width} height={height} aria-hidden>
          <g transform={`translate(${width / 2} ${height - 1}) rotate(${turn}) scale(${h / 32}) translate(-12 -31)`}>
            <path d="M12 31 C12 31 2 18.5 2 11 A10 10 0 0 1 22 11 C22 18.5 12 31 12 31 Z" fill={s.fill} fillOpacity={opacity} stroke={s.stroke} strokeWidth={Math.max(0.5, sw)} />
            <circle cx={12} cy={11} r={3.6} fill="#ffffff" fillOpacity={0.9} />
          </g>
        </svg>
      );
    }
    if (s.marker === 'cross') {
      const a = r * 0.9;
      return (
        <svg width={width} height={height} aria-hidden>
          <g transform={`translate(${width / 2} ${height / 2}) rotate(${turn})`}>
            <path d={`M${-a} ${-a} L${a} ${a} M${a} ${-a} L${-a} ${a}`} stroke={s.fill} strokeOpacity={opacity} strokeWidth={Math.max(2, sw + 1)} />
          </g>
        </svg>
      );
    }
    const shape = markerPath(s.marker, Math.min(r, height / 2 - 1));
    return (
      <svg width={width} height={height} aria-hidden>
        <g transform={`translate(${width / 2} ${height / 2}) rotate(${turn})`} fill={s.fill} fillOpacity={opacity}
           stroke={sw > 0 ? s.stroke : 'none'} strokeWidth={sw}>
          {shape ?? <circle r={Math.min(r, height / 2 - 1)} />}
        </g>
      </svg>
    );
  }
  if (s.geometry === 'line') {
    const w = Math.max(1, Math.min(10, s.size ?? 2));
    const y = height / 2;
    const dash = dashArray(s.dash, w)?.join(' ');
    return (
      <svg width={width} height={height} aria-hidden>
        {s.casing && <line x1={1} y1={y} x2={width - 1} y2={y} stroke={s.casing} strokeWidth={w + 2} />}
        <line x1={1} y1={y} x2={width - 1} y2={y} stroke={s.fill} strokeOpacity={opacity} strokeWidth={w} strokeDasharray={dash} />
        {s.arrows && <polygon points={`${width / 2 + 4},${y} ${width / 2 - 3},${y - 4} ${width / 2 - 3},${y + 4}`} fill={s.fill} stroke="#ffffff" strokeWidth={0.8} />}
      </svg>
    );
  }
  const sw = s.strokeWidth ?? 1;
  const pattern = s.pattern && s.pattern !== 'solid' && !s.outlineOnly ? s.pattern : null;
  const fill = s.outlineOnly ? 'none' : pattern ? `url(#p${id})` : s.fill;
  return (
    <svg width={width} height={height} aria-hidden>
      {pattern && (
        <defs>
          <pattern id={`p${id}`} width={6} height={6} patternUnits="userSpaceOnUse">
            {pattern === 'dots' ? <circle cx={3} cy={3} r={1.2} fill={s.fill} /> : (
              <>
                <path d="M0 6 L6 0 M-1.5 1.5 L1.5 -1.5 M4.5 7.5 L7.5 4.5" stroke={s.fill} strokeWidth={1} />
                {pattern === 'cross-hatch' && <path d="M0 0 L6 6 M-1.5 4.5 L1.5 7.5 M4.5 -1.5 L7.5 1.5" stroke={s.fill} strokeWidth={1} />}
              </>
            )}
          </pattern>
        </defs>
      )}
      <rect x={2} y={2} width={width - 4} height={height - 4} rx={2} fill={fill} fillOpacity={pattern ? 1 : opacity}
            stroke={sw > 0 ? s.stroke : 'none'} strokeWidth={Math.min(3, sw)} strokeDasharray={dashArray(s.dash, Math.min(3, sw))?.join(' ')} />
    </svg>
  );
};

export default StyleSwatch;
